import { createContext, useContext, useState, useEffect } from 'react';

const QuoteContext = createContext();

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
};

export const QuoteProvider = ({ children }) => {
  const [quoteItems, setQuoteItems] = useState([]);
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    venue: '',
    guestCount: ''
  });

  // Load from localStorage on mount
  useEffect(() => {
    const savedQuoteItems = localStorage.getItem('quoteItems');
    const savedClientInfo = localStorage.getItem('clientInfo');

    if (savedQuoteItems) {
      try {
        setQuoteItems(JSON.parse(savedQuoteItems));
      } catch (error) {
        console.error('Error parsing saved quote items:', error);
      }
    }

    if (savedClientInfo) {
      try {
        setClientInfo(JSON.parse(savedClientInfo));
      } catch (error) {
        console.error('Error parsing saved client info:', error);
      }
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('quoteItems', JSON.stringify(quoteItems));
  }, [quoteItems]);

  useEffect(() => {
    localStorage.setItem('clientInfo', JSON.stringify(clientInfo));
  }, [clientInfo]);

  const addToQuote = (item) => {
    setQuoteItems(prevItems => {
      // Check if item already exists (same product and variant)
      const existingIndex = prevItems.findIndex(existing =>
        existing.product === item.product &&
        existing.variant === item.variant
      );

      if (existingIndex >= 0) {
        // Update existing item
        const updatedItems = [...prevItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + item.quantity,
          rentalPeriod: item.rentalPeriod,
          totalPrice: (item.unitPrice * (updatedItems[existingIndex].quantity + item.quantity) * item.rentalPeriod)
        };
        return updatedItems;
      } else {
        // Add new item with unique ID
        return [...prevItems, { ...item, id: Date.now() + Math.random() }];
      }
    });
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      setQuoteItems(items => items.filter(item => item.id !== itemId));
    } else {
      setQuoteItems(items =>
        items.map(item =>
          item.id === itemId ? {
            ...item,
            quantity: newQuantity,
            totalPrice: item.unitPrice * newQuantity * item.rentalPeriod
          } : item
        )
      );
    }
  };

  const updateRentalPeriod = (itemId, newPeriod) => {
    setQuoteItems(items =>
      items.map(item =>
        item.id === itemId ? {
          ...item,
          rentalPeriod: newPeriod,
          totalPrice: item.unitPrice * item.quantity * newPeriod
        } : item
      )
    );
  };

  const removeItem = (itemId) => {
    setQuoteItems(items => items.filter(item => item.id !== itemId));
  };

  const clearQuote = () => {
    setQuoteItems([]);
    setClientInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      eventType: '',
      eventDate: '',
      venue: '',
      guestCount: ''
    });
  };

  const updateClientInfo = (info) => {
    setClientInfo(prev => ({ ...prev, ...info }));
  };

  const getQuoteTotal = () => {
    return quoteItems.reduce((total, item) => total + item.totalPrice, 0);
  };

  const value = {
    quoteItems,
    clientInfo,
    addToQuote,
    updateQuantity,
    updateRentalPeriod,
    removeItem,
    clearQuote,
    updateClientInfo,
    getQuoteTotal
  };

  return (
    <QuoteContext.Provider value={value}>
      {children}
    </QuoteContext.Provider>
  );
};